import { Link } from 'react-router-dom';
import IgnisIcon from '../components/icons/IgnisIcon';
import Simulator from '../components/simulation/Simulator';
import { useAuth } from '../contexts/AuthProvider';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

// The fire-spread simulator is not public: by design, access is granted only on
// request and reserved to official bodies (municipalities, civil protection,
// fire services, ICNF). This avoids misuse such as arson reconnaissance.
const CONTACT_EMAIL = 'institucional@tutela.land';
const MAILTO = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
  'Pedido de acesso ao Simulador IGNIS',
)}`;

const ELIGIBLE = [
  'Municípios e câmaras municipais',
  'Proteção Civil (ANEPC)',
  'Corpos de bombeiros',
  'ICNF',
  'Outras entidades oficiais de gestão de emergência e ordenamento do território',
];

function AccessBanner() {
  const { user } = useAuth();
  return (
    <div className="mb-8 rounded-xl border border-ember/40 bg-ember/10 px-5 py-4">
      {user ? (
        <p className="text-sm leading-relaxed text-mist">
          A sua conta (<span className="font-mono text-ember">{user.email}</span>) ainda não tem
          acesso ao simulador. Solicite acesso abaixo.
        </p>
      ) : (
        <p className="text-sm leading-relaxed text-mist">
          O acesso ao simulador requer uma conta autorizada.{' '}
          <Link to="/entrar" className="font-semibold text-ember hover:underline">
            Entrar
          </Link>
          {' '}ou solicite acesso abaixo.
        </p>
      )}
    </div>
  );
}

function RestrictedExplainer() {
  return (
    <div className="min-h-[calc(100vh-4rem)] bg-ink-950 text-mist">
      <div className="mx-auto max-w-3xl px-6 py-20">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-ember/30 bg-ember/10 px-4 py-1.5 font-mono text-sm font-semibold text-ember">
          <IgnisIcon className="h-4 w-4" />
          IGNIS · Simulação
        </div>

        <h1 className="font-display text-3xl font-bold tracking-tight text-mist sm:text-4xl">
          Simulação de propagação de incêndio
        </h1>

        <p className="mt-6 text-lg leading-relaxed text-mist-muted">
          O simulador estima como um incêndio se poderia propagar a partir de um ponto de ignição,
          com base no terreno, no combustível e nas condições meteorológicas. É uma ferramenta de
          apoio ao planeamento e à resposta operacional.
        </p>

        <div className="mt-10">
          <AccessBanner />
        </div>

        {/* Restricted access notice */}
        <div className="rounded-xl border border-ember/40 bg-ember/5 p-6">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-ember">Acesso restrito</div>
          <p className="mt-3 leading-relaxed text-mist">
            Por decisão ética, o simulador não está disponível publicamente. O acesso é concedido
            apenas a pedido e reservado a entidades oficiais, para evitar usos indevidos.
          </p>
        </div>

        {/* Who can request */}
        <div className="mt-10">
          <h2 className="font-display text-xl font-bold text-mist">Quem pode pedir acesso</h2>
          <ul className="mt-4 space-y-2">
            {ELIGIBLE.map((item) => (
              <li key={item} className="flex items-start gap-3 text-mist-muted">
                <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-ember" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* How to request */}
        <div className="mt-10 rounded-xl border border-ink-700 bg-ink-900 p-6">
          <h2 className="font-display text-xl font-bold text-mist">Como pedir acesso</h2>
          <p className="mt-3 leading-relaxed text-mist-muted">
            Contacte-nos por email, a partir de um endereço institucional, indicando a entidade que
            representa, a sua função e o objetivo da utilização. Responderemos com os próximos passos.
          </p>
          <a
            href={MAILTO}
            className="mt-5 inline-flex items-center gap-2.5 rounded-lg bg-ember px-6 py-3 font-semibold text-ink-950 shadow-[0_0_30px_-8px_#F26A4B] transition-all duration-200 hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ember"
          >
            Solicitar acesso
          </a>
          <p className="mt-4 font-mono text-sm text-mist-faint">{CONTACT_EMAIL}</p>
        </div>

        {/* Public alternative */}
        <p className="mt-10 text-sm text-mist-muted">
          É público em geral?{' '}
          <Link to="/ignis/mapa-nacional" className="font-medium text-ember hover:brightness-110">
            Consulte o mapa nacional de risco
          </Link>
          , disponível para todos.
        </p>
      </div>
    </div>
  );
}

export default function Simulation() {
  useDocumentMeta(
    'Simulação de propagação de incêndio (acesso restrito) | Tutela IGNIS',
    'Ferramenta de simulação de propagação de incêndio reservada a entidades oficiais (municípios, Proteção Civil, bombeiros, ICNF). Acesso a pedido.',
    undefined, // gated: no canonical, noindex
    { noindex: true },
  );

  const { access, loading } = useFeatureAccess('simulation');

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-ink-950 flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-2 border-ink-700 border-t-ember animate-spin" />
      </div>
    );
  }

  return access ? <Simulator /> : <RestrictedExplainer />;
}
